import { createClient } from '@supabase/supabase-js';
import { CampCategory } from '../types/database';

// Ensure these are defined in your .env file
// In production (Netlify Functions), process.env is used (via esbuild --define)
// In development, import.meta.env is used
const supabaseUrl = (typeof process !== 'undefined' && process.env?.VITE_SUPABASE_URL) || import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = (typeof process !== 'undefined' && process.env?.VITE_SUPABASE_ANON_KEY) || import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  const errorMsg = `Missing Supabase environment variables. URL: ${!!supabaseUrl}, Key: ${!!supabaseAnonKey}`;
  console.error('🔍 [SUPABASE]', errorMsg);
  console.error('🔍 [SUPABASE] process.env keys:', typeof process !== 'undefined' ? Object.keys(process.env).filter(k => k.includes('SUPABASE')) : 'N/A');
  throw new Error(errorMsg);
}

// Store original fetch before any overrides
const originalFetch = typeof window !== 'undefined' ? window.fetch : fetch;

// Create a single client instance
// In SSR, disable session persistence and auto-refresh to avoid localStorage access
const isSSR = typeof window === 'undefined';
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: !isSSR, // Disable in SSR
    autoRefreshToken: !isSSR, // Disable in SSR
    detectSessionInUrl: !isSSR, // Disable in SSR
  },
  global: {
    fetch: (input, init) => {
      const requestUrl = typeof input === 'string' ? input : input instanceof Request ? input.url : '';
      const nextInit: RequestInit = {
        ...init
      };

      if (requestUrl.includes('/rest/v1/camps') && (init?.method ?? 'GET').toUpperCase() !== 'GET') {
        const headers = new Headers(init?.headers ?? {});
        if (!headers.has('Prefer')) {
          headers.set('Prefer', 'resolution=merge-duplicates');
        }
        nextInit.headers = headers;
      }

      // Use original fetch to avoid conflicts with window.fetch overrides
      return originalFetch(input, nextInit);
    }
  }
});

// Add authentication state listener only on client side
if (!isSSR) {
  supabase.auth.onAuthStateChange((event, session) => {
    console.log('Auth state changed:', event, session?.user?.id);
  });
}

// Add this type for Google user metadata
interface GoogleUserMetadata {
  avatar_url: string;
  email: string;
  email_verified: boolean;
  full_name: string;
  iss: string;
  name: string;
  picture: string;
  provider_id: string;
  sub: string;
}

// Add a helper function to get user profile
export async function getUserProfile() {
  const { data: { user } } = await supabase.auth.getUser();
  
  if (!user) return null;

  // Get the metadata which includes Google profile info
  const metadata = user.user_metadata as GoogleUserMetadata;
  
  // Log to verify we're getting the data
  console.log('User Metadata:', metadata);

  return {
    id: user.id,
    email: user.email,
    fullName: metadata.full_name,
    avatarUrl: metadata.avatar_url,
    // Add any other fields you need
  };
}

// Category-related functions
export async function getCategoriesGroupedByType() {
  const { data, error } = await supabase
    .from('camp_categories')
    .select('*')
    .order('name');

  if (error) {
    console.error('Error fetching categories:', error);
    return null;
  }

  // Group categories by type
  const grouped = data.reduce((acc, category) => {
    const type = category.category_type || 'other';
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(category);
    return acc;
  }, {} as Record<string, typeof data>);

  return grouped;
}

export async function getCampCategories(campId: string): Promise<CampCategory[]> {
  try {
    const { data: relations, error: relationsError } = await supabase
      .from('camp_category_relations')
      .select(`
        category_id,
        camp_categories (
          id,
          name,
          description,
          category_type,
          created_at
        )
      `)
      .eq('camp_id', campId);

    if (relationsError) {
      console.error('Error fetching camp categories:', relationsError);
      return [];
    }

    return relations?.map(relation => relation.camp_categories).filter(Boolean).flat() || [];
  } catch (error) {
    console.error('Error in getCampCategories:', error);
    return [];
  }
}

export async function updateCampCategories(campId: string, categoryIds: string[]): Promise<boolean> {
  try {
    // First, delete existing relations
    const { error: deleteError } = await supabase
      .from('camp_category_relations')
      .delete()
      .eq('camp_id', campId);

    if (deleteError) {
      console.error('Error deleting existing camp categories:', deleteError);
      return false;
    }

    // Then insert new relations
    if (categoryIds.length > 0) {
      const relations = categoryIds.map(categoryId => ({
        camp_id: campId,
        category_id: categoryId
      }));

      const { error: insertError } = await supabase
        .from('camp_category_relations')
        .insert(relations);

      if (insertError) {
        console.error('Error inserting camp categories:', insertError);
        return false;
      }
    }

    return true;
  } catch (error) {
    console.error('Error in updateCampCategories:', error);
    return false;
  }
}